import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';

import type { ContourProps } from './contour-definitions';
import type {
  Point,
  PointArray,
  PointSi,
  PointSiArray,
} from './types/contour-definition-array-type';
import type { MaskData } from '@/src/store/analysis/biomarker-mask';

import { ContourBase } from './contour-definitions';
import {
  isEqualPoint,
  processPoints,
} from './contour-processing/contourProcessing';
import { getPolygonAreaAndCentroid } from './contour-processing/getPolygonAreaAndCentroid';

export abstract class AreaContourBase extends ContourBase {
  private _needSiRecalculation: boolean = true;
  private _needAreaRecalculation: boolean = true;

  private _meanSignalIntensity: number = 0;
  private _medianSignalIntensity: number = 0;
  private _stdvSignalIntensity: number = 0;
  private _stdvWeightedSignalIntensity: number = 0;
  private _maxSignalIntensity: number = Number.NEGATIVE_INFINITY;
  private _minSignalIntensity: number = Number.POSITIVE_INFINITY;
  private _maxSignalIntensityFullPixel: number = Number.NEGATIVE_INFINITY;
  private _minSignalIntensityFullPixel: number = Number.POSITIVE_INFINITY;
  private _sumSignalIntensity: number = 0;
  private _completeInImagePixels: PointSiArray = [];
  private _fiftyPercentAndMoreInImagePixels: PointSiArray = [];
  private _partialInImagePixels: PointSiArray = [];
  private _subPixels: PointSiArray = [];
  private _halfSubPixels: PointSiArray = [];
  private _maxSignalIntensityPixel: PointSi = { x: -1, y: -1, si: 0 };
  private _minSignalIntensityPixel: PointSi = { x: -1, y: -1, si: 0 };
  private _maxSignalIntensityFullPixelPixel: PointSi = { x: -1, y: -1, si: 0 };
  private _minSignalIntensityFullPixelPixel: PointSi = { x: -1, y: -1, si: 0 };
  private _histogram: Record<number, number> = {};

  private _areaSigned: number = 0;

  private _centroid: Point = { x: -1, y: -1 };

  private _debugImageUrl: string = '';

  private isOpenPolygon: boolean = false;

  constructor(
    props: Partial<ContourProps> & Pick<ContourProps, 'type' | 'label'>,
    polygon: PointArray = [],
    isOpenPolygon: boolean = false,
  ) {
    super(props);
    this.isOpenPolygon = isOpenPolygon;
    this.setPolygonScaled(polygon);
  }

  // todo was private
  public resetSignalIntensityStatistic() {
    this._needSiRecalculation = true;
    this._meanSignalIntensity = 0;
    this._medianSignalIntensity = 0;
    this._stdvSignalIntensity = 0;
    this._stdvWeightedSignalIntensity = 0;
    this._maxSignalIntensity = Number.NEGATIVE_INFINITY;
    this._minSignalIntensity = Number.POSITIVE_INFINITY;
    this._maxSignalIntensityFullPixel = Number.NEGATIVE_INFINITY;
    this._minSignalIntensityFullPixel = Number.POSITIVE_INFINITY;
    this._sumSignalIntensity = 0;
    this._completeInImagePixels = [];
    this._fiftyPercentAndMoreInImagePixels = [];
    this._partialInImagePixels = [];
    this._subPixels = [];
    this._halfSubPixels = [];
    this._maxSignalIntensityPixel = { x: -1, y: -1, si: 0 };
    this._minSignalIntensityPixel = { x: -1, y: -1, si: 0 };
    this._maxSignalIntensityFullPixelPixel = { x: -1, y: -1, si: 0 };
    this._minSignalIntensityFullPixelPixel = { x: -1, y: -1, si: 0 };
    this._histogram = {};
  }

  private resetAreaCalculation() {
    this._needAreaRecalculation = true;
    this._areaSigned = 0;
    this._centroid = { x: -1, y: -1 };
  }

  public setPolygon(polygon: PointArray) {
    this._polygon = [];
    if (!this.isOpenPolygon && polygon.length > 0) {
      if (!isEqualPoint(polygon[0], polygon[polygon.length - 1])) {
        polygon.push(polygon[0]);
      }
    }
    this.resetSignalIntensityStatistic();
    this.resetAreaCalculation();
    this._polygon = polygon;
    this.postSetPolygon();
  }

  public setPolygonScaled(polygon: PointArray) {
    if (!this.isOpenPolygon && polygon.length > 0) {
      if (!isEqualPoint(polygon[0], polygon[polygon.length - 1])) {
        polygon.push(polygon[0]);
      }
    }

    polygon = processPoints(
      polygon,
      !this.isOpenPolygon,
      this.getImageWidth(),
      this.getImageHeight(),
    );

    // // better performance with reassigment ?
    // // eslint-disable-next-line no-param-reassign
    // polygon = interpolateGridPoints2D(polygon, ContourBase.subMatrix);
    // // eslint-disable-next-line no-param-reassign
    // polygon = snapPolyLineToSubPixelGrid(polygon, !this.isOpenPolygon);

    this._polygon = [];
    this.resetSignalIntensityStatistic();
    this.resetAreaCalculation();

    this._polygon = Array.from({ length: polygon.length });
    for (let i = 0; i < polygon.length; i++) {
      this._polygon[i] = {
        x: polygon[i].x * ContourBase.subMatrix,
        y: polygon[i].y * ContourBase.subMatrix,
      };
    }
    this.postSetPolygonScaled();
  }

  protected abstract postSetPolygon(): void;
  protected abstract postSetPolygonScaled(): void;

  // protected static scaleModifyPolygonScaled(polygon: PointArray): PointArray {
  //     polygon.pop(); // remove last point
  //     let modifyPolygon = interpolateGridPoints2D(polygon, ContourBase.subMatrix);
  //     modifyPolygon = snapPolyLineToSubPixelGrid(modifyPolygon);
  //     for (let i = 0; i < modifyPolygon.length; i++) {
  //         modifyPolygon[i].x *= ContourBase.subMatrix;
  //         modifyPolygon[i].y *= ContourBase.subMatrix;
  //       }
  //     return modifyPolygon;
  // }

  public getPolygonScaled() {
    const polygon = Array.from<Point>({ length: this._polygon.length });
    for (let i = 0; i < polygon.length; i++) {
      polygon[i] = {
        x: this._polygon[i].x / ContourBase.subMatrix,
        y: this._polygon[i].y / ContourBase.subMatrix,
      };
    }
    return polygon;
  }

  public getCentroid() {
    if (this._needAreaRecalculation) this.calculateAreaAndCentroid();
    return this._centroid;
  }

  public getCentroidScaled() {
    if (this._needAreaRecalculation) this.calculateAreaAndCentroid();
    return {
      x: this._centroid.x / ContourBase.subMatrix,
      y: this._centroid.y / ContourBase.subMatrix,
    };
  }

  public isValid() {
    return this._polygon.length >= 3 && this.getArea() > 0;
  }

  public getAreaSigned() {
    if (this._needAreaRecalculation) this.calculateAreaAndCentroid();
    return this._areaSigned;
  }

  public getArea() {
    return Math.abs(this.getAreaSigned());
  }

  public getAreaRealWorldvalue() {
    const spacing = this.getPixelSpacing();
    return (
      (this.getArea() / (ContourBase.subMatrix * ContourBase.subMatrix))
      * spacing[0]
      * spacing[1]
    );
  }

  private cleansedPolygonForAreaCalculation(): PointArray {
    // remove any unidentified points
    return this._polygon.filter(point => point);
  }

  private calculateAreaAndCentroid() {
    this.resetAreaCalculation();

    // closing open polygon for area calculation
    if (this.isOpenPolygon) {
      this._polygon.push(this._polygon[0]);
    }

    // TODO: This was added as getPolygonAreaAndCentroid() was failing on some myo fusion contours
    // with unidentified points.
    // After debuging, the contours had 1 unidentified point at
    // around the same place where the epi and endo are connected.
    // further investigation needed why that point is undefined.
    // For now cleanse the polygon before area calculation.
    this._polygon = this.cleansedPolygonForAreaCalculation();

    const { signedArea, centroid } = getPolygonAreaAndCentroid(this._polygon);

    if (this.isOpenPolygon) {
      this._polygon.pop();
    }

    this._areaSigned = signedArea;
    this._centroid = centroid;
    this._needAreaRecalculation = false;
  }

  // getters for SI statistics
  public getMeanSignalIntensity(
    imageData: vtkImageData = this.getVtKImageData(),
  ): number {
    this.checkSiRecalculation(imageData);
    return this._meanSignalIntensity;
  }

  public getMedianSignalIntensity(
    imageData: vtkImageData = this.getVtKImageData(),
  ): number {
    this.checkSiRecalculation(imageData);
    return this._medianSignalIntensity;
  }

  public getStdvSignalIntensity(
    imageData: vtkImageData = this.getVtKImageData(),
  ): number {
    this.checkSiRecalculation(imageData);
    return this._stdvSignalIntensity;
  }

  public getStdvWeightedSignalIntensity(
    imageData: vtkImageData = this.getVtKImageData(),
  ): number {
    this.checkSiRecalculation(imageData);
    return this._stdvWeightedSignalIntensity;
  }

  public getMaxSignalIntensity(
    imageData: vtkImageData = this.getVtKImageData(),
  ): number {
    this.checkSiRecalculation(imageData);
    return this._maxSignalIntensity;
  }

  public getMinSignalIntensity(
    imageData: vtkImageData = this.getVtKImageData(),
  ): number {
    this.checkSiRecalculation(imageData);
    return this._minSignalIntensity;
  }

  public getMaxSignalIntensityFullPixel(
    imageData: vtkImageData = this.getVtKImageData(),
  ): number {
    this.checkSiRecalculation(imageData);
    return this._maxSignalIntensityFullPixel;
  }

  public getMinSignalIntensityFullPixel(
    imageData: vtkImageData = this.getVtKImageData(),
  ): number {
    this.checkSiRecalculation(imageData);
    return this._minSignalIntensityFullPixel;
  }

  public getSumSignalIntensity(
    imageData: vtkImageData = this.getVtKImageData(),
  ): number {
    this.checkSiRecalculation(imageData);
    return this._sumSignalIntensity;
  }

  public getCompleteInImagePixels(
    imageData: vtkImageData = this.getVtKImageData(),
  ): PointSiArray {
    this.checkSiRecalculation(imageData);
    return this._completeInImagePixels;
  }

  public getFiftyPercentAndMoreInImagePixels(
    imageData: vtkImageData = this.getVtKImageData(),
  ): PointSiArray {
    this.checkSiRecalculation(imageData);
    return this._fiftyPercentAndMoreInImagePixels;
  }

  public getPartialInImagePixels(
    imageData: vtkImageData = this.getVtKImageData(),
  ): PointSiArray {
    this.checkSiRecalculation(imageData);
    return this._partialInImagePixels;
  }

  public getSubPixels(
    imageData: vtkImageData = this.getVtKImageData(),
  ): PointSiArray {
    this.checkSiRecalculation(imageData);
    return this._subPixels;
  }

  public getHalfSubPixels(
    imageData: vtkImageData = this.getVtKImageData(),
  ): PointSiArray {
    this.checkSiRecalculation(imageData);
    return this._halfSubPixels;
  }

  public getMaxSignalIntensityPixel(
    imageData: vtkImageData = this.getVtKImageData(),
  ): PointSi {
    this.checkSiRecalculation(imageData);
    return this._maxSignalIntensityPixel;
  }

  public getMinSignalIntensityPixel(
    imageData: vtkImageData = this.getVtKImageData(),
  ): PointSi {
    this.checkSiRecalculation(imageData);
    return this._minSignalIntensityPixel;
  }

  public getMaxSignalIntensityFullPixelPixel(
    imageData: vtkImageData = this.getVtKImageData(),
  ): PointSi {
    this.checkSiRecalculation(imageData);
    return this._maxSignalIntensityFullPixelPixel;
  }

  public getMinSignalIntensityFullPixelPixel(
    imageData: vtkImageData = this.getVtKImageData(),
  ): PointSi {
    this.checkSiRecalculation(imageData);
    return this._minSignalIntensityFullPixelPixel;
  }

  public getHistogram(
    imageData: vtkImageData = this.getVtKImageData(),
  ): Record<number, number> {
    this.checkSiRecalculation(imageData);
    return this._histogram;
  }

  public getSignalIntensityStatistic(
    imageData: vtkImageData = this.getVtKImageData(),
  ) {
    this.checkSiRecalculation(imageData);
    return {
      mean: this._meanSignalIntensity,
      median: this._medianSignalIntensity,
      stdv: this._stdvSignalIntensity,
      max: this._maxSignalIntensity,
      min: this._minSignalIntensity,
    };
  }

  public getMaskPixelValueStatistic(
    mask: MaskData,
    imageData: vtkImageData = this.getVtKImageData(),
  ): {
    mean: number;
    median: number;
    stdv: number;
    stdvWeighted: number;
    max: number;
    min: number;
    histogram: Record<number, number>;
  } {
    // ensure pixel partition (complete / sub / half-sub) is up to date
    this.checkSiRecalculation(imageData);

    // lookup "x,y" -> mask value. NOTE: mask.coords are stored as [y, x].
    const maskMap = new Map<string, number>();
    for (let i = 0; i < mask.coords.length; i += 1) {
      const [my, mx] = mask.coords[i];
      maskMap.set(`${mx},${my}`, mask.values[i]);
    }

    const { subMatrix } = AreaContourBase;
    const halfSubPixelFraction = 1 / (subMatrix * subMatrix * 2);

    // accumulators (same math as calculateSignalIntensity), all local
    const params = {
      imagePixelCount: 0,
      sumSi: 0,
      sumWeightedSi: 0,
      sumQuadrSi: 0,
      sumQuadrSiWeighted: 0,
      sumQuadrWeight: 0,
      n: 0,
    };
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    const histogram: Record<number, number> = {};

    const addIfInMask = (imgX: number, imgY: number, fraction: number) => {
      const value = maskMap.get(`${imgX},${imgY}`);
      if (value === undefined) return; // pixel not in mask -> ignore
      params.imagePixelCount += fraction;
      params.n += 1;
      params.sumSi += value;
      params.sumWeightedSi += value * fraction;
      params.sumQuadrSi += value * value;
      params.sumQuadrSiWeighted += value * value * fraction;
      params.sumQuadrWeight += fraction * fraction;
      if (value < min) min = value;
      if (value > max) max = value;
      histogram[value] = (histogram[value] ?? 0) + fraction;
    };

    // complete pixels: already image-pixel coords
    this._completeInImagePixels.forEach(p => addIfInMask(p.x, p.y, 1));
    // sub / half-sub pixels: sub-pixel coords -> divide back to image space
    this._subPixels.forEach(p =>
      addIfInMask(
        Math.floor(p.x / subMatrix),
        Math.floor(p.y / subMatrix),
        halfSubPixelFraction * 2,
      ),
    );
    this._halfSubPixels.forEach(p =>
      addIfInMask(
        Math.floor(p.x / subMatrix),
        Math.floor(p.y / subMatrix),
        halfSubPixelFraction,
      ),
    );

    if (params.n === 0) {
      return {
        mean: 0,
        median: 0,
        stdv: 0,
        stdvWeighted: 0,
        max: 0,
        min: 0,
        histogram,
      };
    }

    const mean = params.sumWeightedSi / params.imagePixelCount;

    let stdv = 0;
    let stdvWeighted = 0;
    if (params.n > 1) {
      stdvWeighted = Math.sqrt(
        Math.max(
          0,
          (params.sumQuadrSiWeighted * params.imagePixelCount
            - params.sumWeightedSi * params.sumWeightedSi)
          / (params.imagePixelCount * params.imagePixelCount
            - params.sumQuadrWeight),
        ),
      );
      stdv = Math.sqrt(
        Math.max(
          0,
          (1 / (params.n - 1))
          * (params.sumQuadrSi - (params.sumSi * params.sumSi) / params.n),
        ),
      );
    }

    // weighted median over the value histogram
    const medianThreshold = params.imagePixelCount / 2;
    const isEven = params.imagePixelCount % 2 === 0;
    const keys = Object.keys(histogram)
      .map(Number)
      .sort((a, b) => a - b);
    let count = 0;
    let prevKey = 0;
    let median = 0;

    for (const key of keys) {
      count += histogram[key];
      if (count > medianThreshold) {
        median
          = isEven && count - histogram[key] <= medianThreshold
            ? (key + prevKey) / 2
            : key;
        break;
      }
      prevKey = key;
    }

    return { mean, median, stdv, stdvWeighted, min, max, histogram };
  }

  private checkSiRecalculation(imageData: vtkImageData | undefined) {
    if (!imageData) {
      this.resetSignalIntensityStatistic();
      return;
    }
    if (this._needSiRecalculation) this.calculateSignalIntensity(imageData);
  }

  public getDebugImage() {
    return this._debugImageUrl;
  }

  private calculateSignalIntensity(imageData: vtkImageData | undefined) {
    this.resetSignalIntensityStatistic();

    // detect open contour
    // this is not supported for SI calculation, one would need to close in subpixel resolution
    if (this.isOpenPolygon) {
      console.error('Contour: Open polygon not supported for SI calculation');
      return;
    }

    if (!this.isValid()) {
      return;
    }
    if (!imageData) {
      console.error('Contour: No image data provided');
      return;
    }

    // bounding rects
    const { minX, minY, width, height } = this.calculateBoundingRect(
      this._polygon,
    );

    const minXImage = Math.floor(minX / AreaContourBase.subMatrix);
    const minYImage = Math.floor(minY / AreaContourBase.subMatrix);
    const widthImage = Math.ceil(width / AreaContourBase.subMatrix);
    const heightImage = Math.ceil(height / AreaContourBase.subMatrix);

    // check which image pixels at the border
    const lineImagePixels = [] as PointArray;
    const linePixelSet = new Set<string>();

    for (let i = 0; i < this._polygon.length - 1; i++) {
      const p1 = this._polygon[i];
      const p2 = this._polygon[i + 1];

      const midX = (p1.x + p2.x) / 2;
      const midY = (p1.y + p2.y) / 2;

      // are we inside a pixel, then collect
      const restX = midX % AreaContourBase.subMatrix;
      const restY = midY % AreaContourBase.subMatrix;

      if (restX > 0 && restY > 0) {
        const pixX = Math.floor(midX / AreaContourBase.subMatrix);
        const pixY = Math.floor(midY / AreaContourBase.subMatrix);
        const key = `${pixX},${pixY}`;
        if (!linePixelSet.has(key)) {
          linePixelSet.add(key);
          lineImagePixels.push({ x: pixX, y: pixY });
        }
      }
    }

    // inflate the polygon and create a binary image
    const mapWidth = (width + minX + AreaContourBase.subMatrix + 1) * 2;
    const mapHeight = (height + minY + AreaContourBase.subMatrix + 1) * 2;

    // Inflate the polygon points
    const inflatedPolygon = this._polygon.map(point => ({
      x: (point.x - minX) * 2,
      y: (point.y - minY) * 2,
    }));

    const inflatedPolygonImage = this.createInflatedPolygonMask(
      inflatedPolygon,
      minX,
      minY,
      mapWidth,
      mapHeight,
    );

    interface StatParams {
      imagePixelCount: number;
      sumSi: number;
      sumWeightedSi: number;
      sumQuadrSi: number;
      sumQuadrSiWeighted: number;
      sumQuadrWeight: number;
      n: number;
    }

    function createStatParams(): StatParams {
      return {
        imagePixelCount: 0,
        sumSi: 0,
        sumWeightedSi: 0,
        sumQuadrSi: 0,
        sumQuadrSiWeighted: 0,
        sumQuadrWeight: 0,
        n: 0,
      };
    }

    // Accumulates one sample into params (mutates in place).
    function updateCalculationParams(
      params: StatParams,
      si: number,
      fraction: number,
    ): void {
      params.imagePixelCount += fraction;
      params.n += 1;
      params.sumSi += si;
      params.sumWeightedSi += si * fraction;
      params.sumQuadrSi += si * si;
      params.sumQuadrSiWeighted += si * si * fraction;
      params.sumQuadrWeight += fraction * fraction;
    }

    const params = createStatParams();

    const isMaskSet = (x: number, y: number): boolean => {
      if (x >= 0 && x < mapWidth && y >= 0 && y < mapHeight) {
        return inflatedPolygonImage[y * mapWidth + x] === 1;
      }
      return false;
    };

    // determine all image pixels complete inside
    for (let y = minYImage; y < minYImage + heightImage; y++) {
      for (let x = minXImage; x < minXImage + widthImage; x++) {
        const key = `${x},${y}`;

        // on line will get subpixel count
        if (linePixelSet.has(key)) {
          continue;
        }

        // check if inside assuming either complete in or out
        const mapPixX = x * AreaContourBase.subMatrix * 2 + 1 - minX * 2;
        const mapPixY = y * AreaContourBase.subMatrix * 2 + 1 - minY * 2;

        if (isMaskSet(mapPixX, mapPixY)) {
          const imgPix: Point = { x, y };
          const si = this.getPixelSignalIntensity(imageData, imgPix);
          this.setMinMaxSignalIntensity(si, imgPix, true);
          updateCalculationParams(params, si, 1);
          this.setHistogramData(si, 1);
          this._completeInImagePixels.push({ x: imgPix.x, y: imgPix.y, si });
        }
      }
    }

    // tackle line pixels
    lineImagePixels.forEach((pixel) => {
      const upperLeftMapPixX = pixel.x * AreaContourBase.subMatrix;
      const upperLeftMapPixY = pixel.y * AreaContourBase.subMatrix;

      let subPixelCountPixel = 0;
      const completeSubPix = [] as PointArray;
      const halfSubPix = [] as PointArray;

      for (
        let y = upperLeftMapPixY;
        y < upperLeftMapPixY + AreaContourBase.subMatrix;
        y++
      ) {
        const centerPixY = y * 2 + 1 - minY * 2;
        for (
          let x = upperLeftMapPixX;
          x < upperLeftMapPixX + AreaContourBase.subMatrix;
          x++
        ) {
          const centerPixX = x * 2 + 1 - minX * 2;

          if (!isMaskSet(centerPixX, centerPixY)) {
            // complete subpixel outside
            continue;
          }

          // check complete or half subpixel
          let sum = 0;
          if (isMaskSet(centerPixX, centerPixY - 1)) {
            sum++;
          }
          if (isMaskSet(centerPixX, centerPixY + 1)) {
            sum++;
          }
          if (isMaskSet(centerPixX - 1, centerPixY)) {
            sum++;
          }
          if (isMaskSet(centerPixX + 1, centerPixY)) {
            sum++;
          }
          // half or complete
          if (sum === 4) {
            completeSubPix.push({ x, y });
            subPixelCountPixel += 1;
          }
          else {
            halfSubPix.push({ x, y });
            subPixelCountPixel += 0.5;
          }
        }
      }

      const si = this.getPixelSignalIntensity(imageData, pixel);

      if (
        subPixelCountPixel
        === AreaContourBase.subMatrix * AreaContourBase.subMatrix
      ) {
        updateCalculationParams(params, si, 1);
        this.setHistogramData(si, 1);
        this.setMinMaxSignalIntensity(si, pixel, true);
        this._completeInImagePixels.push({ x: pixel.x, y: pixel.y, si });
      }
      else {
        this._partialInImagePixels.push({ x: pixel.x, y: pixel.y, si });

        const halfSubPixelfraction
          = 1 / (AreaContourBase.subMatrix * AreaContourBase.subMatrix * 2);
        let fraction = 0;
        completeSubPix.forEach((pointCompl) => {
          fraction += halfSubPixelfraction * 2;
          this._subPixels.push({ x: pointCompl.x, y: pointCompl.y, si });
        });
        halfSubPix.forEach((pointHalf) => {
          fraction += halfSubPixelfraction;
          this._halfSubPixels.push({ x: pointHalf.x, y: pointHalf.y, si });
        });

        // add to fifty percent and more in image pixels
        if (fraction >= 0.5) {
          this._fiftyPercentAndMoreInImagePixels.push({
            x: pixel.x,
            y: pixel.y,
            si,
          });
        }

        updateCalculationParams(params, si, fraction);
        this.setHistogramData(si, fraction);
        this.setMinMaxSignalIntensity(si, pixel, false);
      }
    });

    // SI statistics
    this._meanSignalIntensity = params.sumWeightedSi / params.imagePixelCount;
    this._sumSignalIntensity = params.sumWeightedSi;

    // stdv
    if (params.n > 1) {
      this._stdvWeightedSignalIntensity = Math.sqrt(
        (params.sumQuadrSiWeighted * params.imagePixelCount
          - params.sumWeightedSi * params.sumWeightedSi)
        / (params.imagePixelCount * params.imagePixelCount
          - params.sumQuadrWeight),
      );

      this._stdvSignalIntensity = Math.sqrt(
        (1 / (params.n - 1))
        * (params.sumQuadrSi - (params.sumSi * params.sumSi) / params.n),
      );
    }

    // median
    const medianThreshold = params.imagePixelCount / 2;
    const isEven = params.imagePixelCount % 2 === 0;
    const siKeys = Object.keys(this._histogram).map(Number);
    siKeys.sort((a, b) => a - b);

    let count = 0;
    let prevKey = 0;
    let median = 0;

    for (const key of siKeys) {
      count += this._histogram[key];
      if (count > medianThreshold) {
        if (isEven && count - this._histogram[key] <= medianThreshold) {
          // For even totalPixels, return the average of the two middle values
          median = (key + prevKey) / 2;
        }
        median = key;
      }
      prevKey = key;
    }
    this._medianSignalIntensity = median;
    this._needSiRecalculation = false;
  }

  private createInflatedPolygonMask(
    inflatedPolygon: PointArray,
    minX: number,
    minY: number,
    mapWidth: number,
    mapHeight: number,
  ): Uint8Array {
    const buffer = new Uint8Array(mapWidth * mapHeight);

    const n = inflatedPolygon.length;
    if (n < 3) return buffer;

    let minYfill = inflatedPolygon[0].y;
    let maxYfill = inflatedPolygon[0].y;

    // find min and max y to limit scanline range
    for (let i = 1; i < n; i++) {
      if (inflatedPolygon[i].y < minYfill) minYfill = inflatedPolygon[i].y;
      if (inflatedPolygon[i].y > maxYfill) maxYfill = inflatedPolygon[i].y;
    }

    const startY = Math.max(0, minYfill);
    const endY = Math.min(mapHeight - 1, maxYfill);

    // scanline fill algorithm
    for (let y = startY; y <= endY; y++) {
      const intersections: number[] = [];

      // intersections with polygon edges
      for (let i = 0; i < n; i++) {
        const p1 = inflatedPolygon[i];
        const p2 = inflatedPolygon[(i + 1) % n];

        if (p1.y === p2.y) continue; // Skip horizontal edges

        if ((y >= p1.y && y < p2.y) || (y >= p2.y && y < p1.y)) {
          const x = Math.round(
            p1.x + ((y - p1.y) * (p2.x - p1.x)) / (p2.y - p1.y),
          );
          intersections.push(x);
        }
      }

      // sort intersections and fill pixels between pairs
      intersections.sort((a, b) => a - b);
      const rowOffset = y * mapWidth;
      for (let i = 0; i < intersections.length; i += 2) {
        if (i + 1 >= intersections.length) break;
        const x1 = Math.max(0, Math.min(mapWidth - 1, intersections[i]));
        const x2 = Math.max(0, Math.min(mapWidth - 1, intersections[i + 1]));

        if (x2 >= x1) {
          buffer.fill(1, rowOffset + x1, rowOffset + x2 + 1);
        }
      }
    }

    return buffer;
  }

  private boolArrayToDataUrl(
    image: Uint8Array | boolean[],
    width: number,
    height: number,
  ): string {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Cannot get 2D context from canvas.');
      return '';
    }

    const imageData = ctx.createImageData(width, height);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const index = y * width + x;
        const isFilled = Boolean(image[index]);

        const pixelIndex = index * 4;
        const color = isFilled ? 0 : 255;
        imageData.data[pixelIndex] = color;
        imageData.data[pixelIndex + 1] = color;
        imageData.data[pixelIndex + 2] = color;
        imageData.data[pixelIndex + 3] = 255;
      }
    }

    // Put the ImageData object onto the canvas
    ctx.putImageData(imageData, 0, 0);

    // Convert the canvas to a Data URL
    return canvas.toDataURL();
  }

  /* original opencv based version, does not work for vitest */
  /*
    private calculateSignalIntensity(imageData: vtkImageData | undefined) {

        this.resetSignalIntensityStatistic();

        if (!this.isValid()) {
            return;
        }
        if (!imageData) {
            console.error('Contour: No image data provided');
            return;
        }

        // bounding rects
        const { minX, minY, width, height } = this.calculateBoundingRect(this._polygon);

        const minXImage = Math.floor(minX / AreaContourBase.subMatrix);
        const minYImage = Math.floor(minY / AreaContourBase.subMatrix);
        const widthImage = Math.ceil(width / AreaContourBase.subMatrix);
        const heightImage = Math.ceil(height / AreaContourBase.subMatrix);

        // check which image pixels at the border
        const lineImagePixels = [] as PointArray;
        for (let i = 0; i < this._polygon.length - 1; i++) {
            const p1 = this._polygon[i];
            const p2 = this._polygon[i + 1];

            const midpoint: Point = {
                x: Math.floor((p1.x + p2.x) / 2),
                y: Math.floor((p1.y + p2.y) / 2),
            };

            // are we inside a pixel, then collect
            const restX = midpoint.x % AreaContourBase.subMatrix;
            const restY = midpoint.y % AreaContourBase.subMatrix;

            if (restX > 0 && restY > 0) {
                const pix: Point = {
                    x: Math.floor(midpoint.x / AreaContourBase.subMatrix),
                    y: Math.floor(midpoint.y / AreaContourBase.subMatrix),
                };
                if (!lineImagePixels.some(p => p.x === pix.x && p.y === pix.y)) {
                    lineImagePixels.push(pix);
                }
            }
        }
        console.log('lineImagePixels', lineImagePixels);

        // inflate the polygon and create a binary image
        const inflatedPolygonImage = this.drawInflatedPolygonOnImage(this._polygon, minX, minY, width, height);

        // debug image
        this._debugImageUrl = this.matToDataUrl(inflatedPolygonImage);

        type StatParams = {
            imagePixelCount: number;
            sumSi: number;
            sumWeightedSi: number;
            sumQuadrSi: number;
            sumQuadrSiWeighted: number;
            sumQuadrWeight: number;
            n: number;
        };

        function updateCalculationParams(params: StatParams, si: number, fraction: number): StatParams {
            const paramsUpdate = params;
            paramsUpdate.imagePixelCount += fraction;
            paramsUpdate.n++;
            paramsUpdate.sumSi += si;
            paramsUpdate.sumWeightedSi += si * fraction;
            paramsUpdate.sumQuadrSi += si * si;
            paramsUpdate.sumQuadrSiWeighted = si * si * fraction;
            paramsUpdate.sumQuadrWeight += fraction * fraction;

            return paramsUpdate;
        }

        let params = {
            imagePixelCount: 0,
            sumSi: 0,
            sumWeightedSi: 0,
            sumQuadrSi: 0,
            sumQuadrSiWeighted: 0,
            sumQuadrWeight: 0,
            n: 0,
        };

        // determine all image pixels complete inside
        for (let y = minYImage; y < minYImage + heightImage; y++) {
            for (let x = minXImage; x < minXImage + widthImage; x++) {
                const imgPix: Point = { x, y };

                // on line will get subpixel count
                if (lineImagePixels.some(p => p.x === imgPix.x && p.y === imgPix.y)) {
                    // console.log('lineImagePixelsSet has imgPix', imgPix);
                    // eslint-disable-next-line no-continue
                    continue;
                }

                // check if inside assuming either complete in or out
                const mapPix: Point = {
                    x: x * AreaContourBase.subMatrix * 2 + 1 - minX * 2,
                    y: y * AreaContourBase.subMatrix * 2 + 1 - minY * 2,
                };

                const idx = mapPix.y * inflatedPolygonImage.cols + mapPix.x;
                if (inflatedPolygonImage.ucharAt(idx) === 0) { // black inside
                    // console.log('In Pix', imgPix);

                    const si = this.getPixelSignalIntensity(imageData, imgPix);
                    this.setMinMaxSignalIntensity(si, imgPix);
                    params = updateCalculationParams(params, si, 1);
                    this.setHistogramData(si, 1);
                    this._completeInImagePixels.push({ x: imgPix.x, y: imgPix.y, si });
                }
            }
        }

        // console.log('completeInsideImagePixels', completeInsideImagePixels);

        // tackle line pixels
        lineImagePixels.forEach(pixel => {
            const upperLeftMapPix: Point = {
                x: pixel.x * AreaContourBase.subMatrix,
                y: pixel.y * AreaContourBase.subMatrix,
            };

            let subPixelCountPixel = 0;
            const completeSubPix = [] as PointArray;
            const halfSubPix = [] as PointArray;

            // eslint-disable-next-line prefer-destructuring
            for (let y = upperLeftMapPix.y; y < upperLeftMapPix.y + AreaContourBase.subMatrix; y++) {
                // eslint-disable-next-line prefer-destructuring
                for (let x = upperLeftMapPix.x; x < upperLeftMapPix.x + AreaContourBase.subMatrix; x++) {
                    const centerPix: Point = {
                        x: x * 2 + 1 - minX * 2,
                        y: y * 2 + 1 - minY * 2,
                    };
                    let idx = centerPix.y * inflatedPolygonImage.cols + centerPix.x;
                    if (inflatedPolygonImage.ucharAt(idx) > 0) {
                        // complete subpixel outside
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    // check complete or half subpixel
                    let sum = 0;
                    idx = (centerPix.y - 1) * inflatedPolygonImage.cols + centerPix.x;
                    if (inflatedPolygonImage.ucharAt(idx) === 0) {
                        sum++;
                    }
                    idx = (centerPix.y + 1) * inflatedPolygonImage.cols + centerPix.x;
                    if (inflatedPolygonImage.ucharAt(idx) === 0) {
                        sum++;
                    }
                    idx = centerPix.y * inflatedPolygonImage.cols + centerPix.x - 1;
                    if (inflatedPolygonImage.ucharAt(idx) === 0) {
                        sum++;
                    }
                    idx = centerPix.y * inflatedPolygonImage.cols + centerPix.x + 1;
                    if (inflatedPolygonImage.ucharAt(idx) === 0) {
                        sum++;
                    }
                    // half or complete
                    if (sum === 4) {
                        completeSubPix.push({ x, y });
                        subPixelCountPixel += 1;
                    }
                    else {
                        halfSubPix.push({ x, y });
                        subPixelCountPixel += 0.5;
                    }
                }
            }

            const si = this.getPixelSignalIntensity(imageData, pixel);
            this.setMinMaxSignalIntensity(si, pixel);

            if (subPixelCountPixel === AreaContourBase.subMatrix * AreaContourBase.subMatrix) {
                params = updateCalculationParams(params, si, 1);
                this.setHistogramData(si, 1);
                this._completeInImagePixels.push({ x: pixel.x, y: pixel.y, si });
            }
            else {
                this._partialInImagePixels.push({ x: pixel.x, y: pixel.y, si });

                const halfSubPixelfraction = 1 / (AreaContourBase.subMatrix * AreaContourBase.subMatrix * 2);
                let fraction = 0;
                completeSubPix.forEach(pointCompl => {
                    fraction += halfSubPixelfraction * 2;
                    this._subPixels.push({ x: pointCompl.x, y: pointCompl.y, si });

                });
                halfSubPix.forEach(pointHalf => {
                    fraction += halfSubPixelfraction;
                    this._halfSubPixels.push({ x: pointHalf.x, y: pointHalf.y, si });
                });

                params = updateCalculationParams(params, si, fraction);
                this.setHistogramData(si, fraction);
            }
        });

        // SI statistics
        this._meanSignalIntensity = params.sumWeightedSi / params.imagePixelCount;
        this._sumSignalIntensity = params.sumWeightedSi;

        // stdv
        if (params.n > 1) {
            this._stdvWeightedSignalIntensity =
                Math.sqrt((params.sumQuadrSiWeighted * params.imagePixelCount - params.sumWeightedSi * params.sumWeightedSi)
                    / (params.imagePixelCount * params.imagePixelCount - params.sumQuadrWeight));

            this._stdvSignalIntensity =
                Math.sqrt(1. / (params.n - 1)
                    * (params.sumQuadrSi - params.sumSi * params.sumSi / params.n));
        }

        // median
        const medianThreshold = params.imagePixelCount / 2;
        const isEven = params.imagePixelCount % 2 === 0;
        const siKeys = Object.keys(this._histogram).map(Number);
        siKeys.sort((a, b) => a - b);

        let count = 0;
        let prevKey = 0;
        let median = 0;
        // eslint-disable-next-line no-restricted-syntax
        for (const key of siKeys) {
            count += this._histogram[key];
            if (count > medianThreshold) {
                if (isEven && count - this._histogram[key] <= medianThreshold) {
                    // For even totalPixels, return the average of the two middle values
                    median = (key + prevKey) / 2;
                }
                median = key;
            }
            prevKey = key;
        }
        this._medianSignalIntensity = median;

        console.log('meanSignalIntensity', this._meanSignalIntensity);
        // console.log('medianSignalIntensity', this._medianSignalIntensity);
        // console.log('stdvSignalIntensity', this._stdvSignalIntensity);
        // console.log('stdvWeightedSignalIntensity', this._stdvWeightedSignalIntensity);
        // console.log('maxSignalIntensity', this._maxSignalIntensity);
        // console.log('minSignalIntensity', this._minSignalIntensity);
        // console.log('sumSignalIntensity', this._sumSignalIntensity);
        // console.log('area', params.imagePixelCount);
        console.log('completeInsideImagePixels', this._completeInImagePixels);
        // console.log('partialInsideImagePixels', this._partialInImagePixels);
        // console.log('subPixels', this._subPixels);
        // console.log('halfSubPixels', this._halfSubPixels);

        this._needSiRecalculation = false;
    }

    // eslint-disable-next-line class-methods-use-this
    private drawInflatedPolygonOnImage(polygon: PointArray, minX: number, minY: number,
        width: number, height: number): cv.Mat {
        const mapWidth = (width + minX + AreaContourBase.subMatrix + 1) * 2;
        const mapHeight = (height + minY + AreaContourBase.subMatrix + 1) * 2;
        // binary image filled with white - no real binary in opencv.js

        const image = new cv.Mat(mapHeight, mapWidth, cv.CV_8UC1, new cv.Scalar(255));
        // inflate, polygon points for OpenCV

        const polyPoints = polygon.map(point => [(point.x - minX) * 2, (point.y - minY) * 2]);
        // format accepted by OpenCV.js fillPoly

        const polyArray = cv.matFromArray(polyPoints.length, 1, cv.CV_32SC2, polyPoints.flat());
        // draw the filled polygon on the binary image
        const pts = new cv.MatVector();

        pts.push_back(polyArray);

        cv.fillPoly(image, pts, new cv.Scalar(0)); // black color

        // Cleanup
        polyArray.delete();
        pts.delete();

        return image;
    }

    // Convert OpenCV Mat to a Data URL
    // eslint-disable-next-line class-methods-use-this
    private matToDataUrl(mat: cv.Mat): string {
        const canvas = document.createElement('canvas');
        canvas.width = mat.cols;
        canvas.height = mat.rows;

        // Check if the Mat is not empty
        if (mat.empty()) {
            console.error('Cannot convert an empty Mat to a Data URL.');
            return '';
        }

        cv.imshow(canvas, mat);

        // Ensure the canvas has content before converting to a data URL
        if (!canvas.getContext('2d')) {
            console.error('Canvas context is not available.');
            return '';
        }
        return canvas.toDataURL();
    }
    */

  private calculateBoundingRect(points: PointArray) {
    if (points.length === 0) {
      return { minX: 0, minY: 0, width: 0, height: 0 };
    }

    let minX = points[0].x;
    let maxX = points[0].x;
    let minY = points[0].y;
    let maxY = points[0].y;

    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }

    const width = maxX - minX + 1;
    const height = maxY - minY + 1;

    return { minX, minY, width, height };
  }

  private setHistogramData(si: number, fraction: number) {
    if (this._histogram[si]) {
      this._histogram[si] += fraction;
    }
    else {
      this._histogram[si] = fraction;
    }
  }

  private setMinMaxSignalIntensity(
    si: number,
    point: Point,
    isFullPixel: boolean = false,
  ) {
    if (si > this._maxSignalIntensity) {
      this._maxSignalIntensityPixel = { x: point.x, y: point.y, si };
      this._maxSignalIntensity = si;
    }
    if (si < this._minSignalIntensity) {
      this._minSignalIntensityPixel = { x: point.x, y: point.y, si };
      this._minSignalIntensity = si;
    }
    if (isFullPixel) {
      if (si > this._maxSignalIntensityFullPixel) {
        this._maxSignalIntensityFullPixelPixel = { x: point.x, y: point.y, si };
        this._maxSignalIntensityFullPixel = si;
      }
      if (si < this._minSignalIntensityFullPixel) {
        this._minSignalIntensityFullPixelPixel = { x: point.x, y: point.y, si };
        this._minSignalIntensityFullPixel = si;
      }
    }
  }

  // length calculation for fractional shortening

  // ─── Convex Hull (Andrew's Monotone Chain) ───────────────────────────

  protected static convexHull(points: PointArray): PointArray {
    const pts = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
    if (pts.length <= 1) return pts;

    const cross = (o: Point, a: Point, b: Point) =>
      (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

    const lower: Point[] = [];
    for (let i = 0; i < pts.length; i++) {
      while (
        lower.length >= 2
        && cross(lower[lower.length - 2], lower[lower.length - 1], pts[i]) <= 0
      ) {
        lower.pop();
      }
      lower.push(pts[i]);
    }

    const upper: Point[] = [];
    for (let i = pts.length - 1; i >= 0; i--) {
      while (
        upper.length >= 2
        && cross(upper[upper.length - 2], upper[upper.length - 1], pts[i]) <= 0
      ) {
        upper.pop();
      }
      upper.push(pts[i]);
    }

    lower.pop();
    upper.pop();
    return [...lower, ...upper];
  }

  // ─── Anisotropic Distance ────────────────────────────────────────────

  protected static anisotropicDist(
    a: Point,
    b: Point,
    sx: number,
    sy: number,
  ): number {
    const dx = (b.x - a.x) * sx;
    const dy = (b.y - a.y) * sy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // ─── Debug Visualization ─────────────────────────────────────────────

  protected static debugDrawConvexHullSpline(
    polygon: PointArray,
    hull: PointArray,
    hullPerimeter: number,
    _closedHull: boolean = true,
  ) {
    const allPoints: PointArray = [...polygon, ...hull];
    const xs = allPoints.map(p => p.x);
    const ys = allPoints.map(p => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const pad = 20;
    const scale = 2;
    const w = Math.ceil((maxX - minX) * scale) + pad * 2;
    const h = Math.ceil((maxY - minY) * scale) + pad * 2;

    const tx = (p: Point) => (p.x - minX) * scale + pad;
    const ty = (p: Point) => (p.y - minY) * scale + pad;

    const popup = window.open(
      '',
      '_blank',
      `width=${Math.max(w + 40, 320)},height=${Math.max(h + 80, 240)}`,
    );
    if (!popup) return;

    const canvas = popup.document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    popup.document.body.style.margin = '8px';
    popup.document.body.style.background = '#1e1e1e';
    popup.document.body.appendChild(canvas);

    const info = popup.document.createElement('div');
    info.style.color = '#ccc';
    info.style.fontFamily = 'monospace';
    info.style.fontSize = '12px';
    info.style.marginTop = '6px';
    info.innerHTML
      = `hull: ${hull.length} pts &nbsp;|&nbsp; `
        + `<b>perimeter: ${hullPerimeter.toFixed(2)} mm</b>`;
    popup.document.body.appendChild(info);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#1e1e1e';
    ctx.fillRect(0, 0, w, h);

    // original polygon — dark grey fill
    ctx.beginPath();
    polygon.forEach((p, i) => {
      if (i === 0) ctx.moveTo(tx(p), ty(p));
      else ctx.lineTo(tx(p), ty(p));
    });
    ctx.closePath();
    ctx.fillStyle = 'rgba(255,255,255,0.07)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // convex hull — cyan outline
    ctx.beginPath();
    hull.forEach((p, i) => {
      if (i === 0) ctx.moveTo(tx(p), ty(p));
      else ctx.lineTo(tx(p), ty(p));
    });
    // if (closedHull) ctx.closePath();
    ctx.strokeStyle = 'cyan';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // hull vertices — cyan dots
    ctx.fillStyle = 'cyan';
    hull.forEach((p) => {
      ctx.beginPath();
      ctx.arc(tx(p), ty(p), 3, 0, Math.PI * 2);
      ctx.fill();
    });
  }
}
